import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, ButtonText, Flex, FormField, TextInput } from '@percent/lemonade';
import { ErrorVariant } from '@percent/domain/verifications';
import { Card } from '@percent/hosted-validation-form/ui';
import { ErrorCard } from '@percent/hosted-validation-form/ui/ErrorCard/ErrorCard';
import { useOtp, useOtpState, usePartnerConfig, useProgress } from '@percent/hosted-validation-form/hooks';
import { ValidationSubmissionArguments } from '@percent/hosted-validation-form/api/actions/validationSubmissionAction';
import { useHostedValidationsFormAnalytics } from '@percent/hosted-validation-form/hooks/useHostedValidationsFormAnalytics/useHostedValidationsFormAnalytics';
import { useValidationStepData } from '../../validationStepContext/useValidationStepData/useValidationStepData';
import { HostedFormStep } from '../../HostedForm.types';
import { getStepAfterOtp } from '../stepFlow';

const OTP_LENGTH = 6;
const DIGITS_ONLY = /^\d*$/;

export const Otp = () => {
  const { handleNext, handleBack } = useValidationStepData();
  const { t } = useTranslation();
  const { progress } = useProgress();
  const { partnerConfig } = usePartnerConfig();
  const { setValue } = useFormContext<ValidationSubmissionArguments>();
  const { otpCooldownEndsAt, mustReverifyOtp, setMustReverifyOtp } = useOtpState();
  const { track } = useHostedValidationsFormAnalytics();

  const [code, setCode] = useState('');
  const [maxAttemptsExceeded, setMaxAttemptsExceeded] = useState(false);
  const hasAutoSent = useRef(false);

  const {
    sendOtp,
    verifyOtp,
    clearVerifyError,
    clearSendError,
    cooldownSeconds,
    canResend,
    isVerifying,
    verifyErrorKey,
    sendErrorKey,
  } = useOtp({
    onVerifySuccess: (otpJwt) => {
      setValue('otpJwt', otpJwt);
      setMustReverifyOtp(false);
      // On the expired-JWT bounce every other step is already filled in, so send the
      // user straight back to terms rather than through partner fields again.
      handleNext(mustReverifyOtp ? HostedFormStep.TERMS : getStepAfterOtp(partnerConfig));
    },
    onMaxAttemptsExceeded: () => setMaxAttemptsExceeded(true),
  });

  // Auto-send once per mount unless an unexpired cooldown is active.
  useEffect(() => {
    if (hasAutoSent.current) {
      return;
    }

    hasAutoSent.current = true;

    const cooldownActive = otpCooldownEndsAt !== null && otpCooldownEndsAt > Date.now();

    if (!cooldownActive) {
      sendOtp();
    }
  }, [otpCooldownEndsAt, sendOtp]);

  // Banner only makes sense in the immediate JWT-expired bounce flow, so clear it on
  // the way out rather than on unmount - StrictMode's double-mount would wipe it
  // before anyone saw it.
  const handleNavigateBack = () => {
    setMustReverifyOtp(false);
    handleBack();
  };

  const handleCodeChange = (next: string) => {
    if (!DIGITS_ONLY.test(next)) {
      return;
    }

    setCode(next);

    if (verifyErrorKey) {
      clearVerifyError();
    }
  };

  const handleResend = () => {
    if (!canResend) {
      return;
    }

    track('OTP code resent', { step: HostedFormStep.OTP });
    setCode('');
    clearVerifyError();
    clearSendError();
    sendOtp();
  };

  if (maxAttemptsExceeded) {
    return (
      <ErrorCard
        title={t('hostedValidation.otp.maxAttemptsExceeded.title')}
        subtitle={t('hostedValidation.otp.maxAttemptsExceeded.subtitle')}
        variant={ErrorVariant.OTP_MAX_ATTEMPTS_EXCEEDED}
      />
    );
  }

  const activeErrorKey = sendErrorKey ?? verifyErrorKey;

  return (
    <Card
      onBack={handleNavigateBack}
      title={t('hostedValidation.otp.title')}
      description={t('hostedValidation.otp.description')}
      progress={progress}
      onNext={() => verifyOtp(code)}
      nextButtonLabel={t('hostedValidation.otp.verifyButton')}
      nextButtonDisabled={code.length !== OTP_LENGTH}
      isLoading={isVerifying}
    >
      <Flex direction="column" gap={16} w="100%">
        {mustReverifyOtp && <Alert variant="warning">{t('hostedValidation.otp.mustReverify')}</Alert>}
        <FormField
          label={t('hostedValidation.otp.codeLabel')}
          status={activeErrorKey ? 'danger' : 'default'}
          statusMessage={activeErrorKey ? t(activeErrorKey) : undefined}
          necessity="required"
          hideCharacterCount
        >
          <TextInput
            name="otp-code"
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            placeholder={t('hostedValidation.otp.codePlaceholder')}
            inputMode="numeric"
            maxLength={OTP_LENGTH}
            autoComplete="one-time-code"
          />
        </FormField>
        <ButtonText
          variant="primary"
          size="sm"
          onClick={handleResend}
          disabled={!canResend}
          data-testid="otp-resend-button"
        >
          {cooldownSeconds > 0
            ? t('hostedValidation.otp.resendCountdown', { seconds: cooldownSeconds })
            : t('hostedValidation.otp.resendButton')}
        </ButtonText>
      </Flex>
    </Card>
  );
};
